import { Professional } from "@/hooks/useSearch"
import styles from "./professional-card.module.css"
import { forwardRef } from "react"
import LocationIcon from "@/components/IconsTsx/LocationIcon"
import GrayPhoneIcon from "@/components/IconsTsx/GrayPhoneIcon"
import { trackNumberClicked, trackMapsClicked } from "@/lib/mixpanelClient"
import { formatDistance } from "@/lib/geo"

const PinIcon = () => (
    <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
)

interface ProfessionalCardProps {
    professional: Professional;
    isSelected: boolean;
    onSelect: (professional: Professional) => void;
}

export const ProfessionalCard = forwardRef<HTMLDivElement, ProfessionalCardProps>(({
    professional,
    isSelected,
    onSelect
}, ref) => {

    const handleOpenMaps = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (professional.LOCATION_LAT && professional.LOCATION_LONG) {
            trackMapsClicked({ prestadorId: professional.CODIGO }); // #18
            const url = `https://www.google.com/maps?q=${professional.LOCATION_LAT},${professional.LOCATION_LONG}`;
            window.open(url, '_blank');
        }
    };

    const handleCall = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (professional.TELEFONO) {
            trackNumberClicked({ prestadorId: professional.CODIGO, numero: professional.TELEFONO }); // #17
            window.location.href = `tel:${professional.TELEFONO}`;
        }
    };

    return (
        <div
            className={`${styles.professionalCard} ${isSelected ? styles.selectedCard : ''}`}
            onClick={() => onSelect(professional)}
            ref={ref}
            data-professional-card
            data-professional-id={`${professional.CODIGO}-${professional.NOMBRE}-${professional.APELLIDO}`}
        >
            <div className={styles.professionalHeader}>
                <div className={styles.professionalInfo}>
                    <h4 className={styles.professionalLastName}>
                        {professional.APELLIDO},
                    </h4>
                    <h5 className={styles.professionalName}>
                        {professional.NOMBRE}
                    </h5>
                </div>
                {typeof professional.distanceKm === "number" && (
                    <span className={styles.distanceBadge}>
                        <PinIcon />
                        {formatDistance(professional.distanceKm)}
                    </span>
                )}
                {typeof professional.distanceKm !== "number" &&
                    typeof professional.distanceEstimatedKm === "number" && (
                    <span
                        className={`${styles.distanceBadge} ${styles.distanceBadgeEstimated}`}
                        title="Ubicación aproximada: este prestador no tiene coordenadas exactas"
                    >
                        <PinIcon />
                        ~{formatDistance(professional.distanceEstimatedKm)}
                    </span>
                )}
            </div>

            <div className={styles.professionalDetails}>
                <div className={styles.detailItem}>
                    <LocationIcon />
                    <span>{`${professional.DIRECCION && professional.DIRECCION}, ${professional.LOCALIDAD && professional.LOCALIDAD}`}</span>
                </div>
                {professional.TELEFONO && (
                    <div className={styles.detailItem}>
                        <GrayPhoneIcon />
                        <span>{professional.TELEFONO}</span>
                    </div>
                )}
            </div>

            <div className={styles.professionalActions}>
                {professional.LOCATION_LAT !== 0 && professional.LOCATION_LONG !== 0 && (
                    <button
                        className={`${styles.actionButton} ${styles.mapsButton}`}
                        onClick={handleOpenMaps}
                        disabled={!professional.LOCATION_LAT || !professional.LOCATION_LONG}
                    >
                        Abrir en Maps
                    </button>
                )}
                {professional.TELEFONO && (
                    <button
                        className={`${styles.actionButton} ${styles.callButton}`}
                        onClick={handleCall}
                        disabled={!professional.TELEFONO}
                    >
                        Llamar
                    </button>
                )}
            </div>
        </div>
    );
});

ProfessionalCard.displayName = 'ProfessionalCard'; 